import { injectable } from 'inversify';

import { OperationResult } from '@vp/common/core/OperationResult';
import { RemoveMediaDto } from '@vp/manager/gallery/core/dto/RemoveMediaDto';
import { UploadMediaDto } from '@vp/manager/gallery/core/dto/UploadMediaDto';
import { GalleryManagerPort } from '@vp/manager/gallery/core/interface/GalleryManagerPort';
import { GalleryManagerUploadHandle } from '@vp/manager/gallery/core/interface/GalleryManagerUploadHandle';
import { RemoveMediaUsecase } from '@vp/manager/gallery/core/usecase/RemoveMediaUsecase';
import { UploadMediaUsecase } from '@vp/manager/gallery/core/usecase/UploadMediaUsecase';
import { ProfileManagerState } from '@vp/manager/profile/core/interface/ProfileManagerState';
import { ProfilePhotoModel } from '@vp/profile/core/model/ProfilePhotoModel';

import { MakeLiveUsecase } from './usecase/MakeLiveUsecase';
import { MakeStaticUsecase } from './usecase/MakeStaticUsecase';

@injectable()
export class GalleryManagerFacade implements GalleryManagerPort {
  constructor(
    private readonly uploadMediaUsecase: UploadMediaUsecase,
    private readonly removeMediaUsecase: RemoveMediaUsecase,
    private readonly profileManagerState: ProfileManagerState,
    private readonly makeLiveUsecase: MakeLiveUsecase,
    private readonly makeStaticUsecase: MakeStaticUsecase,
  ) {}

  hasPhotoAnimationAttempts(): boolean {
    return this.profileManagerState.hasPhotoAnimationAttempts();
  }

  async makeStatic(photoId: string, controller?: AbortController): Promise<OperationResult> {
    return this.makeStaticUsecase.execute(photoId, controller);
  }

  getPhotoOfActiveProfileBy(id: string): ProfilePhotoModel {
    return this.profileManagerState.getPhotoOfActiveProfileBy(id);
  }

  async makeLive(photoId: string, controller?: AbortController): Promise<OperationResult> {
    return this.makeLiveUsecase.execute(photoId, controller);
  }

  upload(dto: UploadMediaDto): GalleryManagerUploadHandle {
    return this.uploadMediaUsecase.execute(dto);
  }

  async remove(dto: RemoveMediaDto, controller?: AbortController): Promise<OperationResult> {
    return await this.removeMediaUsecase.execute(dto, controller);
  }
}
